import {
  BuildingOfficeIcon,
  IdentificationIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import type { ReadOrganizationWithEmployees } from '@/services/api/partiesService';
import { enhancedApi } from '@/services/api/partiesService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

export type Organization = ReadOrganizationWithEmployees;

export const OrganizationTooltipContent = memo<
  EntityTooltipContentProps<Organization>
>(({ fetchState, isPersistent }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="organization" />;
  }

  if (fetchState.status === 'idle') {
    return <EmptyState entityType="organization" />;
  }

  const organization = fetchState.data;
  const displayName =
    organization.legal_name ||
    organization.dba_name ||
    organization.short_description ||
    'Unnamed';

  const hasEmployees =
    organization.employees && organization.employees.length > 0;

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader icon={BuildingOfficeIcon} title={displayName} />

      {(organization.dba_name || organization.tax_id) && (
        <TooltipSection withBorder>
          {organization.dba_name && organization.legal_name && (
            <InfoRow
              icon={BuildingOfficeIcon}
              label="DBA"
              value={organization.dba_name}
            />
          )}
          <InfoRow
            icon={IdentificationIcon}
            label="EIN"
            value={organization.tax_id}
          />
        </TooltipSection>
      )}

      {hasEmployees && (
        <TooltipSection withBorder>
          <div className="space-y-1">
            {organization.employees!.map((emp) => {
              const empName = [emp.first_name, emp.last_name]
                .filter(Boolean)
                .join(' ');
              return (
                <InfoRow
                  key={emp.id}
                  icon={UserIcon}
                  label="Employee"
                  value={empName || emp.short_description || 'Unnamed'}
                />
              );
            })}
          </div>
        </TooltipSection>
      )}

      {organization.involvement_description && (
        <TooltipSection withBorder>
          <p className="text-sm text-gray-600">
            {organization.involvement_description}
          </p>
        </TooltipSection>
      )}
    </div>
  );
});

OrganizationTooltipContent.displayName = 'OrganizationTooltipContent';

export const OrganizationLink = createEntityLink<
  Organization,
  { id: string; matterId: string }
>({
  tooltipContent: OrganizationTooltipContent,
  useQuery: enhancedApi.endpoints.organizationsWithEmployees.useQuery,
  getQueryArg: (id) => {
    const [matterId, orgId] = id.split(':');
    return { id: orgId || id, matterId: matterId || id };
  },
  displayName: 'OrganizationLink',
});
